import type { ChangeEventHandler, FC } from 'react'
import { useCallback, useState } from 'react'

import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button'
import { createFanCategoryFx } from '@/dialogs/EditFanOriginDialog/models.ts'
import CheckIcon from '@/modules/chatting/icons/small-check.svg'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'

import { useKeyupHandler } from '../../utils/useKeyupHandler.ts'

export interface Props {}

export const CreateCategory: FC<Props> = () => {
  const isCreating = useUnit(createFanCategoryFx.pending)

  const [value, setValue] = useState('')
  const [mode, setMode] = useState<'button' | 'input'>('button')

  const reset = () => {
    setValue('')
    setMode('button')
  }

  const handleCreate = useCallback(async () => {
    await createFanCategoryFx(value)
    reset()
  }, [value])

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      setValue(event.target.value)
      setTimeout(() => event.target.focus(), 0)
    },
    [],
  )

  const keyupHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 13) {
        handleCreate()
      }
    },
    [handleCreate],
  )

  useKeyupHandler(keyupHandler, mode === 'input')

  return (
    <div className="px-3 py-1.5 text-sm leading-5">
      {mode === 'button' ? (
        <Button
          variant="link"
          className="text-primary-500 h-max p-0 align-middle hover:no-underline"
          onClick={() => setMode('input')}
        >
          <PlusIcon />
          Add Category
        </Button>
      ) : (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            className="border-secondary-100 flex-1 border-b border-solid bg-transparent font-medium outline-none placeholder:text-sm placeholder:font-medium placeholder:!leading-5"
            placeholder="Category name (Max 50 symbols)"
            value={value}
            onChange={handleInputChange}
            disabled={isCreating}
            max={50}
          />
          <IconButton
            icon={CloseIcon}
            disabled={isCreating}
            onClick={reset}
            className="text-secondary-300"
          />
          <IconButton
            icon={CheckIcon}
            disabled={isCreating || !value}
            onClick={handleCreate}
            className="text-primary-500"
          />
        </div>
      )}
    </div>
  )
}
